export interface WorkbenchAttachmentNavigation {
  pageIndex?: number;
  query?: string;
  highlightRects?: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  requestId?: string;
}

export function toAttachmentPath({
  docId,
  blockId,
  navigation,
}: {
  docId: string;
  blockId: string;
  navigation?: WorkbenchAttachmentNavigation;
}) {
  const search = toAttachmentSearchParams(navigation);
  const query = search.size ? `?${search.toString()}` : '';

  return `/${docId}/attachments/${blockId}${query}`;
}

export function toFullscreenAttachmentPath({
  docId,
  blockId,
  navigation,
}: {
  docId: string;
  blockId: string;
  navigation?: WorkbenchAttachmentNavigation;
}) {
  const search = toAttachmentFullscreenSearchParams(blockId, navigation);
  const query = search.size ? `?${search.toString()}` : '';

  return `/${docId}${query}`;
}

export function toAttachmentFullscreenSearchParams(
  blockId: string,
  navigation: WorkbenchAttachmentNavigation | undefined
) {
  const params = new URLSearchParams();
  params.set('mode', 'edgeless');
  params.set('fullscreenAttachmentId', blockId);

  const attachmentParams = toAttachmentSearchParams(navigation);
  const prefixMap: Record<string, string> = {
    pageIndex: 'fullscreenAttachmentPageIndex',
    query: 'fullscreenAttachmentQuery',
    highlightRects: 'fullscreenAttachmentHighlightRects',
    requestId: 'fullscreenAttachmentRequestId',
  };

  attachmentParams.forEach((value, key) => {
    const prefixedKey = prefixMap[key];
    if (prefixedKey) {
      params.set(prefixedKey, value);
    }
  });

  return params;
}

export function toAttachmentSearchParams(
  navigation: WorkbenchAttachmentNavigation | undefined
) {
  const params = new URLSearchParams();
  if (!navigation) return params;

  if (
    navigation.pageIndex !== undefined &&
    Number.isInteger(navigation.pageIndex) &&
    navigation.pageIndex >= 0
  ) {
    params.set('pageIndex', String(navigation.pageIndex));
  }

  const query = navigation.query?.trim();
  if (query) {
    params.set('query', query);
  }

  const highlightRects = navigation.highlightRects?.filter(isHighlightRect);
  if (highlightRects?.length) {
    params.set('highlightRects', JSON.stringify(highlightRects));
  }

  const requestId = navigation.requestId?.trim();
  if (requestId) {
    params.set('requestId', requestId);
  }

  return params;
}

function isHighlightRect(
  rect: NonNullable<WorkbenchAttachmentNavigation['highlightRects']>[number]
): boolean {
  return (
    Number.isFinite(rect.x) &&
    Number.isFinite(rect.y) &&
    Number.isFinite(rect.width) &&
    Number.isFinite(rect.height) &&
    rect.x >= 0 &&
    rect.y >= 0 &&
    rect.width >= 0 &&
    rect.height >= 0 &&
    rect.x <= 1 &&
    rect.y <= 1 &&
    rect.width <= 1 &&
    rect.height <= 1
  );
}
